import { useState, useEffect, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useAppMode } from '../context/DemoContext';

/**
 * Returns `{ requestLiveMode, authModalOpen, closeAuthModal, isLive }`.
 *
 * Calling `requestLiveMode()` opens the LiveModeAuthModal — the modal itself
 * handles the wallet-connect flow + authorization check + auto-promotion to
 * 'live' for authorized wallets, or shows an "Access Denied" panel for
 * non-authorized wallets.
 */
export function useLiveMode() {
  const { connected } = useWallet();
  const { mode, isLive } = useAppMode();
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // If the user is already authorized + in live mode, no modal needed.
  const requestLiveMode = useCallback(() => {
    if (isLive) return; // already live, nothing to do
    setAuthModalOpen(true);
  }, [isLive]);

  const closeAuthModal = useCallback(() => setAuthModalOpen(false), []);

  // Defensive: if mode flips to live while modal is open, close it.
  useEffect(() => {
    if (isLive && authModalOpen) setAuthModalOpen(false);
  }, [isLive, authModalOpen]);

  return { requestLiveMode, authModalOpen, closeAuthModal, isLive: mode === 'live', connected };
}
